import React, { useState, useEffect } from 'react';
import { db, storage } from './firebase';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getHolesByCourse } from './courseData';


function MyProfile({ onClose, userId, currentUserId }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [trophies, setTrophies] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [photoURL, setPhotoURL] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
const [showPlayerSelect, setShowPlayerSelect] = useState(false);
const [allPlayers, setAllPlayers] = useState([]);
const [comparePlayer, setComparePlayer] = useState(null);
const [compareStats, setCompareStats] = useState(null);

  useEffect(() => {
    loadProfileData();
  }, [userId]);

  
  // Load special trophies
useEffect(() => {
  const loadSpecialTrophies = async () => {
    try {
      const trophiesQuery = query(
        collection(db, 'special_trophies'),
        where('userId', '==', userId)
      );
      const trophiesSnapshot = await getDocs(trophiesQuery);
      
      const specialTrophies = trophiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        isSpecial: true // Mark as admin-awarded
      }));
      
      setTrophies(prevTrophies => {
  // Remove any existing special trophies first to avoid duplicates
  const autoTrophies = prevTrophies.filter(t => !t.isSpecial);
  return [...autoTrophies, ...specialTrophies];
});
    } catch (error) {
      console.error('Error loading special trophies:', error);
    }
  };

  if (userId) {
    loadSpecialTrophies();
  }
}, [userId]);

  const loadProfileData = async () => {
    try {
      setLoading(true);

      // Load user profile
      const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', userId)));
      if (!userDoc.empty) {
        const userData = userDoc.docs[0].data();
        setUser(userData);
        setPhotoURL(userData.photoURL || null);
      }

      // Load player's rounds
      const roundsQuery = query(
        collection(db, 'rounds'),
        where('userId', '==', userId),
        where('status', '==', 'approved')
      );

      const roundsSnapshot = await getDocs(roundsQuery);
      
      if (roundsSnapshot.empty) {
        setStats(null);
        setTrophies([]);
        setLoading(false);
        return;
      }

      // Calculate comprehensive stats
      let totalRounds = 0;
      let totalPoints = 0;
      let totalGross = 0;
      let totalPutts = 0;
      let totalGIR = 0;
      let totalFairways = 0;
      let totalFairwayOpps = 0;
      let totalUpDowns = 0;
let totalUpDownOpps = 0;
let totalCigarettes = 0;
      let totalBorrowed = 0;
      let totalDrinks = 0;
      let bestRound = 0;
      let bestGross = 999;
      let perfectGIRCount = 0;

      roundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
        const round = doc.data();
        totalRounds++;
        totalPoints += round.totalStableford || 0;
        totalGross += round.totalGross || 0;
        totalPutts += round.totalPutts || 0;
        totalGIR += round.greensInRegulation || 0;
        totalFairways += round.fairwaysHit || 0;
        totalFairwayOpps += round.fairwaysTotal || 0;
        totalUpDowns += round.upAndDowns || 0;
totalUpDownOpps += round.upAndDownOpportunities || 0;

totalCigarettes += round.cigarettesSmoked || 0;
        totalBorrowed += round.cigarettesBorrowed || 0;
        totalDrinks += round.beersAfterRound || 0;

        if (round.totalStableford > bestRound) bestRound = round.totalStableford;
        if (round.totalGross > 0 && round.totalGross < bestGross) bestGross = round.totalGross;
        if (round.greensInRegulation === 9) perfectGIRCount++;
      });

      // Fetch all rounds for SGP field averages
      const allRoundsQuery = query(collection(db, 'rounds'), where('status', '==', 'approved'));
      const allRoundsSnapshot = await getDocs(allRoundsQuery);

      const puttsByEvent = {};
      allRoundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
        const r = doc.data();
        if (!r.eventId || !r.totalPutts) return;
        if (!puttsByEvent[r.eventId]) puttsByEvent[r.eventId] = { total: 0, count: 0 };
        puttsByEvent[r.eventId].total += r.totalPutts;
        puttsByEvent[r.eventId].count++;
      });
      const fieldAvgPutts = {};
      Object.entries(puttsByEvent).forEach(([eid, val]) => {
        fieldAvgPutts[eid] = val.total / val.count;
      });

      let totalSGP = 0;
      let sgpCount = 0;
      roundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
        const r = doc.data();
        if (r.eventId && fieldAvgPutts[r.eventId] && r.totalPutts) {
          totalSGP += fieldAvgPutts[r.eventId] - r.totalPutts;
          sgpCount++;
        }
      });
      const seasonSGP = sgpCount > 0
        ? parseFloat((totalSGP / sgpCount).toFixed(2))
        : null;

      const calculatedStats = {
        totalRounds,
        avgPoints: Math.round(totalPoints / totalRounds),
        avgGross: Math.round(totalGross / totalRounds),
        avgPutts: (totalPutts / totalRounds).toFixed(1),
        girPercentage: totalRounds > 0 ? Math.round((totalGIR / (totalRounds * 9)) * 100) : 0,
        fairwayPercentage: totalFairwayOpps > 0 ? Math.round((totalFairways / totalFairwayOpps) * 100) : 0,
        upDownPercentage: totalUpDownOpps > 0 ? Math.round((totalUpDowns / totalUpDownOpps) * 100) : 0,
        bestRound,
        bestGross: bestGross === 999 ? 0 : bestGross,
        seasonSGP,
        perfectGIRCount,
        totalCigarettes,
        totalDrinks
      };

      setStats(calculatedStats);

      // Calculate trophies
      await calculateTrophies(calculatedStats, userId);

    } catch (error) {
      console.error('Error loading profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTrophies = async (playerStats, playerId) => {
    const earnedTrophies = [];

    try {
      // Load all approved rounds for comparison
      const allRoundsQuery = query(
        collection(db, 'rounds'),
        where('status', '==', 'approved')
      );
      const allRoundsSnapshot = await getDocs(allRoundsQuery);

      // Calculate season stats for all players
      const playerSeasonStats = {};
      
      allRoundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
        const round = doc.data();
        const uid = round.userId;
        
        if (!playerSeasonStats[uid]) {
          playerSeasonStats[uid] = {
            totalPoints: 0,
            roundsPlayed: 0,
            totalCigarettes: 0,
            totalDrinks: 0,
            totalUpDowns: 0,
            totalUpDownOpps: 0,
            totalFairways: 0,
            totalFairwayOpps: 0
          };
        }
        
        playerSeasonStats[uid].totalPoints += round.totalStableford || 0;
        playerSeasonStats[uid].roundsPlayed++;
        playerSeasonStats[uid].totalCigarettes += round.cigarettesSmoked || 0;
        playerSeasonStats[uid].totalDrinks += round.beersAfterRound || 0;
        playerSeasonStats[uid].totalUpDowns += round.upAndDowns || 0;
        playerSeasonStats[uid].totalUpDownOpps += round.upAndDownOpportunities || 0;
        playerSeasonStats[uid].totalFairways += round.fairwaysHit || 0;
        playerSeasonStats[uid].totalFairwayOpps += round.fairwaysTotal || 0;
      });

      // Calculate averages
      Object.keys(playerSeasonStats).forEach(uid => {
        const stats = playerSeasonStats[uid];
        stats.avgPoints = stats.roundsPlayed > 0 ? stats.totalPoints / stats.roundsPlayed : 0;
        stats.upDownPercentage = stats.totalUpDownOpps > 0 ? (stats.totalUpDowns / stats.totalUpDownOpps) * 100 : 0;
        stats.fairwayPercentage = stats.totalFairwayOpps > 0 ? (stats.totalFairways / stats.totalFairwayOpps) * 100 : 0;
      });

      // Sort players by average points
      const rankedByAvg = Object.entries(playerSeasonStats)
        .sort((a, b) => b[1].avgPoints - a[1].avgPoints);

      // 🥇 Season Champion - only if season has ended
const today = new Date();
const activeSeasonsQuery = query(collection(db, 'seasons'), where('isActive', '==', true));
const activeSeasonsSnapshot = await getDocs(activeSeasonsQuery);
const currentSeason = activeSeasonsSnapshot.empty ? null : activeSeasonsSnapshot.docs[0].data();
const seasonEnded = currentSeason?.endDate ? new Date(currentSeason.endDate) < today : false;

if (seasonEnded && rankedByAvg.length > 0 && rankedByAvg[0][0] === playerId) {
  earnedTrophies.push({
    icon: '🥇',
    name: 'Season Champion',
    description: 'Highest average points',
    year: new Date().getFullYear()
  });
}

      // 🎯 Perfect GIR
      if (playerStats.perfectGIRCount > 0) {
        earnedTrophies.push({
          icon: '🎯',
          name: 'Perfect GIR',
          description: `Hit all 9 greens (${playerStats.perfectGIRCount}x)`,
          year: new Date().getFullYear()
        });
      }

      // 🚬 Chimney Award (most cigarettes)
      const mostCigarettes = Math.max(...Object.values(playerSeasonStats).map(s => s.totalCigarettes));
      if (playerSeasonStats[playerId] && playerSeasonStats[playerId].totalCigarettes === mostCigarettes && mostCigarettes > 0) {
        earnedTrophies.push({
          icon: '🚬',
          name: 'Chimney Award',
          description: `Most cigarettes (${mostCigarettes})`,
          year: new Date().getFullYear()
        });
      }

      // 🍺 Party Animal (most drinks)
      const mostDrinks = Math.max(...Object.values(playerSeasonStats).map(s => s.totalDrinks));
      if (playerSeasonStats[playerId] && playerSeasonStats[playerId].totalDrinks === mostDrinks && mostDrinks > 0) {
        earnedTrophies.push({
          icon: '🍺',
          name: 'Party Animal',
          description: `Most drinks (${mostDrinks})`,
          year: new Date().getFullYear()
        });
      }

      // ⛳ Short Game Wizard (best up & down %)
      const qualifiedUpDown = Object.entries(playerSeasonStats).filter(([_, s]) => s.roundsPlayed >= 5);
      if (qualifiedUpDown.length > 0) {
        const bestUpDown = qualifiedUpDown.sort((a, b) => b[1].upDownPercentage - a[1].upDownPercentage);
        if (bestUpDown[0][0] === playerId) {
          earnedTrophies.push({
            icon: '⛳',
            name: 'Short Game Wizard',
            description: `Best up & down % (${Math.round(bestUpDown[0][1].upDownPercentage)}%)`,
            year: new Date().getFullYear()
          });
        }
      }

      // 🏌️ Sharpshooter (best fairway %)
      const qualifiedFairway = Object.entries(playerSeasonStats).filter(([_, s]) => s.roundsPlayed >= 5);
      if (qualifiedFairway.length > 0) {
        const bestFairway = qualifiedFairway.sort((a, b) => b[1].fairwayPercentage - a[1].fairwayPercentage);
        if (bestFairway[0][0] === playerId) {
          earnedTrophies.push({
            icon: '🏌️',
            name: 'Sharpshooter',
            description: `Best fairway accuracy (${Math.round(bestFairway[0][1].fairwayPercentage)}%)`,
            year: new Date().getFullYear()
          });
        }
      }

      // 🐦 Birdie King - Most birdies in a season
let birdieCount = 0;

// Re-fetch this player's rounds to count birdies
const playerRoundsQuery = query(
  collection(db, 'rounds'),
  where('userId', '==', playerId),
  where('status', '==', 'approved')
);
const playerRoundsSnapshot = await getDocs(playerRoundsQuery);

playerRoundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
  const round = doc.data();
  if (!round.holeData) return;
  
  const courseHoles = getHolesByCourse(round.course);
  
 Object.entries(round.holeData).forEach(([holeNum, holeData]) => {
  if (!holeData.strokes) return;
  const holeInfo = courseHoles.find(h => h.number === parseInt(holeNum));
  if (!holeInfo) return;
  
  // Count GROSS birdies (actual score vs par, no handicap)
  if (holeData.strokes === holeInfo.par - 1) {
    birdieCount++;
  }
});
});

// Eagle, Albatross, and Hole-in-One tracking
let eagleCount = 0;
let albatrossCount = 0;
let holeInOneCount = 0;

playerRoundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
  const round = doc.data();
  if (round.holeData) {
    Object.entries(round.holeData).forEach(([holeNum, holeData]) => {
      if (!holeData.strokes) return;
      const courseHoles = getHolesByCourse(round.course);
      const holeInfo = courseHoles.find(h => h.number === parseInt(holeNum));
      if (!holeInfo) return;
      
      // Eagle (2 under par)
      if (holeData.strokes === holeInfo.par - 2) {
        eagleCount++;
      }
      
      // Albatross (3 under par)
      if (holeData.strokes === holeInfo.par - 3) {
        albatrossCount++;
      }
      
      // Hole-in-One (ace on any hole)
      if (holeData.strokes === 1) {
        holeInOneCount++;
      }
    });
  }
});



if (birdieCount >= 5) {
  earnedTrophies.push({
    icon: '🐦',
    name: 'Birdie King',
    description: `${birdieCount} birdies this season`,
    year: new Date().getFullYear()
  });
}
      setTrophies(prevTrophies => {
  // Keep special trophies, add auto-calculated ones
  const specialTrophies = prevTrophies.filter(t => t.isSpecial);
  return [...specialTrophies, ...earnedTrophies];
});

// Eagle trophy
if (eagleCount > 0) {
  earnedTrophies.push({
    icon: '🦅',
    name: 'Eagle Hunter',
    description: `${eagleCount} eagle${eagleCount !== 1 ? 's' : ''} this season`
  });
}

// Albatross trophy
if (albatrossCount > 0) {
  earnedTrophies.push({
    icon: '🦤',
    name: 'AlbaBoss',
    description: `${albatrossCount} albatross${albatrossCount !== 1 ? 'es' : ''} this season`
  });
}

// Hole-in-One trophy
if (holeInOneCount > 0) {
  earnedTrophies.push({
    icon: '⛳',
    name: 'Hole-in-One Hero',
    description: `${holeInOneCount} hole-in-one${holeInOneCount !== 1 ? 's' : ''} this season`
  });
}

    

    } catch (error) {
      console.error('Error calculating trophies:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB');
      return;
    }

    try {
      setUploading(true);

      // Upload to Firebase Storage
      const storageRef = ref(storage, `profile-photos/${userId}`);
      await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update user document
      await updateDoc(doc(db, 'users', userId), {
        photoURL: downloadURL
      });

      setPhotoURL(downloadURL);
      alert('Profile photo updated!');

    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Error uploading photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const loadAllPlayers = async () => {
  try {
    const usersQuery = query(collection(db, 'users'), where('status', '==', 'approved'));
    const usersSnapshot = await getDocs(usersQuery);
    const players = usersSnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(p => p.id !== userId); // Exclude current user
    setAllPlayers(players);
  } catch (error) {
    console.error('Error loading players:', error);
  }
};

const loadComparePlayerStats = async (playerId) => {
  try {
    const roundsQuery = query(
      collection(db, 'rounds'),
      where('userId', '==', playerId),
      where('status', '==', 'approved')
    );
    const roundsSnapshot = await getDocs(roundsQuery);
    
    if (roundsSnapshot.empty) {
      setCompareStats(null);
      return;
    }

    let totalRounds = 0;
    let totalPoints = 0;
    let totalGross = 0;
    let totalPutts = 0;
    let totalGIR = 0;
    let totalFairways = 0;
    let totalFairwayOpps = 0;
    let totalUpDowns = 0;
    let totalUpDownOpps = 0;
    let bestRound = 0;
    let bestGross = 999;

    roundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
      const round = doc.data();
      totalRounds++;
      totalPoints += round.totalStableford || 0;
      totalGross += round.totalGross || 0;
      totalPutts += round.totalPutts || 0;
      totalGIR += round.greensInRegulation || 0;
      totalFairways += round.fairwaysHit || 0;
      totalFairwayOpps += round.fairwaysTotal || 0;
      totalUpDowns += round.upAndDowns || 0;
      totalUpDownOpps += round.upAndDownOpportunities || 0;

      if (round.totalStableford > bestRound) bestRound = round.totalStableford;
      if (round.totalGross > 0 && round.totalGross < bestGross) bestGross = round.totalGross;
    });

    // Fetch all rounds for SGP field averages
    const allRoundsQuery = query(collection(db, 'rounds'), where('status', '==', 'approved'));
    const allRoundsSnapshot = await getDocs(allRoundsQuery);

    const puttsByEvent = {};
    allRoundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
      const r = doc.data();
      if (!r.eventId || !r.totalPutts) return;
      if (!puttsByEvent[r.eventId]) puttsByEvent[r.eventId] = { total: 0, count: 0 };
      puttsByEvent[r.eventId].total += r.totalPutts;
      puttsByEvent[r.eventId].count++;
    });
    const fieldAvgPutts = {};
    Object.entries(puttsByEvent).forEach(([eid, val]) => {
      fieldAvgPutts[eid] = val.total / val.count;
    });

    let totalSGP = 0;
    let sgpCount = 0;
    roundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
      const r = doc.data();
      if (r.eventId && fieldAvgPutts[r.eventId] && r.totalPutts) {
        totalSGP += fieldAvgPutts[r.eventId] - r.totalPutts;
        sgpCount++;
      }
    });
    const seasonSGP = sgpCount > 0
      ? parseFloat((totalSGP / sgpCount).toFixed(2))
      : null;

    setCompareStats({
      totalRounds,
      avgPoints: Math.round(totalPoints / totalRounds),
      avgGross: Math.round(totalGross / totalRounds),
      avgPutts: (totalPutts / totalRounds).toFixed(1),
      girPercentage: totalRounds > 0 ? Math.round((totalGIR / (totalRounds * 9)) * 100) : 0,
      fairwayPercentage: totalFairwayOpps > 0 ? Math.round((totalFairways / totalFairwayOpps) * 100) : 0,
      upDownPercentage: totalUpDownOpps > 0 ? Math.round((totalUpDowns / totalUpDownOpps) * 100) : 0,
      bestRound,
      bestGross: bestGross === 999 ? 0 : bestGross,
      seasonSGP
    });
  } catch (error) {
    console.error('Error loading compare stats:', error);
  }
};

  if (loading) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h2 style={styles.title}>Loading...</h2>
            <button onClick={onClose} style={styles.closeButton}>✕</button>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h2 style={styles.title}>My Profile</h2>
            <button onClick={onClose} style={styles.closeButton}>✕</button>
          </div>
          <div style={styles.content}>
            <div style={styles.photoSection}>
              <div style={styles.photoContainer}>
                {photoURL ? (
                  <img src={photoURL} alt="Profile" style={styles.photo} />
                ) : (
                  <div style={styles.photoPlaceholder}>
                    {user?.name?.charAt(0) || '?'}
                  </div>
                )}
              </div>
              {userId === currentUserId && (
  <>
    <input
      type="file"
      accept="image/*"
      onChange={handlePhotoUpload}
      style={styles.fileInput}
      id="photo-upload"
      disabled={uploading}
    />
    <label htmlFor="photo-upload" style={styles.uploadButton}>
      {uploading ? 'Uploading...' : 'Change Photo'}
    </label>
  </>
)}
            </div>
            <p style={styles.emptyState}>You haven't completed any rounds yet. Start playing to earn trophies!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.cardLarge}>
        <div style={styles.header}>
  <div>
    <h2 style={styles.title}>{user?.name || 'My Profile'}</h2>
    <p style={styles.subtitle}>{stats.totalRounds} rounds played</p>
  </div>
  <div style={{display: 'flex', gap: '10px'}}>
    <button 
      onClick={() => {
        loadAllPlayers();
        setShowPlayerSelect(true);
      }} 
      style={styles.compareButton}
    >
      ⚔️ Compare
    </button>
    <button onClick={onClose} style={styles.closeButton}>✕</button>
  </div>
</div>

        <div style={styles.content}>
          {/* Profile Photo */}
          <div style={styles.photoSection}>
            <div style={styles.photoContainer}>
              {photoURL ? (
                <img src={photoURL} alt="Profile" style={styles.photo} />
              ) : (
                <div style={styles.photoPlaceholder}>
                  {user?.name?.charAt(0) || '?'}
                </div>
              )}
            </div>
           {userId === currentUserId && (
  <>
    <input
      type="file"
      accept="image/*"
      onChange={handlePhotoUpload}
      style={styles.fileInput}
      id="photo-upload"
      disabled={uploading}
    />
    <label htmlFor="photo-upload" style={styles.uploadButton}>
      {uploading ? 'Uploading...' : 'Change Photo'}
    </label>
  </>
)}
          </div>

          {/* Trophies */}
{trophies.length > 0 && (
  <div style={styles.section}>
    <h3 style={styles.sectionTitle}>🏆 Trophies & Achievements</h3>
    <div style={styles.trophiesGrid}>
      {trophies.map((trophy, index) => (
        <div 
          key={trophy.id || index} 
          style={trophy.isSpecial ? styles.trophyCardSpecial : styles.trophyCard}
        >
          <div style={styles.trophyIcon}>{trophy.icon}</div>
          <div style={trophy.isSpecial ? styles.trophyNameSpecial : styles.trophyName}>
            {trophy.name}
          </div>
          <div style={styles.trophyDescription}>
            {trophy.description || trophy.note}
          </div>
          <div style={styles.trophyYear}>{trophy.year}</div>
          {trophy.isSpecial && (
            <div style={styles.specialBadge}>👑 Special Award</div>
          )}
        </div>
      ))}
    </div>
  </div>
)}

          {/* Performance Stats */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>⛳ Performance Stats</h3>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>Avg Points</div>
                <div style={styles.statValue}>{stats.avgPoints}</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>Best Round</div>
                <div style={styles.statValue}>{stats.bestRound}</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>Avg Gross</div>
                <div style={styles.statValue}>{stats.avgGross}</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>Avg Putts</div>
                <div style={styles.statValue}>{stats.avgPutts}</div>
              </div>
            </div>
          </div>

          {/* Shot Quality */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🎯 Shot Quality</h3>
            <div style={styles.progressBars}>
              <div style={styles.progressItem}>
                <div style={styles.progressLabel}>
                  <span>Greens in Regulation</span>
                  <span style={styles.progressValue}>{stats.girPercentage}%</span>
                </div>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${stats.girPercentage}%`}} />
                </div>
              </div>
              <div style={styles.progressItem}>
                <div style={styles.progressLabel}>
                  <span>Fairway Accuracy</span>
                  <span style={styles.progressValue}>{stats.fairwayPercentage}%</span>
                </div>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${stats.fairwayPercentage}%`}} />
                </div>
              </div>
              <div style={styles.progressItem}>
                <div style={styles.progressLabel}>
                  <span>Up & Down</span>
                  <span style={styles.progressValue}>{stats.upDownPercentage}%</span>
                </div>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${stats.upDownPercentage}%`}} />
                </div>
              </div>
            </div>
          </div>

          {/* Fun Stats */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🎉 Fun Stats</h3>
            <div style={styles.funStatsGrid}>
              <div style={styles.funStatCard}>
                <div style={styles.funStatIcon}>🚬</div>
                <div style={styles.funStatValue}>{stats.totalCigarettes}</div>
                <div style={styles.funStatLabel}>Total Cigarettes</div>
              </div>
              <div style={styles.funStatCard}>
                <div style={styles.funStatIcon}>🍺</div>
                <div style={styles.funStatValue}>{stats.totalDrinks}</div>
                <div style={styles.funStatLabel}>Total Drinks</div>
              </div>
              <div style={styles.funStatCard}>
                <div style={styles.funStatIcon}>🎯</div>
                <div style={styles.funStatValue}>{stats.perfectGIRCount}</div>
                <div style={styles.funStatLabel}>Perfect GIR Rounds</div>
              </div>
            </div>
          </div>
       </div>
      </div>

      {/* Player Selection Modal */}
      {showPlayerSelect && (
        <div style={styles.selectOverlay} onClick={() => setShowPlayerSelect(false)}>
          <div style={styles.selectCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.selectTitle}>Compare With</h3>
            <div style={styles.playersList}>
              {allPlayers.map(player => (
                <button
                  key={player.id}
                  onClick={() => {
                    setComparePlayer(player);
                    loadComparePlayerStats(player.id);
                    setShowPlayerSelect(false);
                    setShowComparison(true);
                  }}
                  style={styles.playerSelectButton}
                >
                  {player.name}
                </button>
              ))}
            </div>
            <button onClick={() => setShowPlayerSelect(false)} style={styles.cancelSelectButton}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Comparison View */}
      {showComparison && comparePlayer && compareStats && (
        <div style={styles.comparisonOverlay} onClick={() => setShowComparison(false)}>
          <div style={styles.comparisonCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.comparisonHeader}>
  <div style={styles.comparisonPlayer}>
    {photoURL ? (
      <img src={photoURL} alt={user?.name} style={styles.comparisonPhoto} />
    ) : (
      <div style={styles.comparisonPhotoPlaceholder}>
        {user?.name?.charAt(0) || '?'}
      </div>
    )}
    <span style={styles.comparisonPlayerName}>{user?.name}</span>
  </div>
  <span style={styles.comparisonVs}>VS</span>
  <div style={styles.comparisonPlayer}>
    {comparePlayer.photoURL ? (
      <img src={comparePlayer.photoURL} alt={comparePlayer.name} style={styles.comparisonPhoto} />
    ) : (
      <div style={styles.comparisonPhotoPlaceholder}>
        {comparePlayer.name?.charAt(0) || '?'}
      </div>
    )}
    <span style={styles.comparisonPlayerName}>{comparePlayer.name}</span>
  </div>
</div>
            
            <div style={styles.comparisonStats}>
  {/* Avg Points */}
  <div style={styles.comparisonRow}>
    <div style={styles.comparisonLabel}>Avg Points</div>
    <div style={styles.comparisonValues}>
      <span style={stats.avgPoints === compareStats.avgPoints ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'right'} : stats.avgPoints > compareStats.avgPoints ? styles.yourValueWinning : styles.yourValueLosing}>
        {stats.avgPoints}
      </span>
      <div style={styles.comparisonBarContainer}>
        <div style={{
          ...(stats.avgPoints >= compareStats.avgPoints ? styles.comparisonBarWinning : styles.comparisonBarLosing),
          width: `${(stats.avgPoints / (stats.avgPoints + compareStats.avgPoints)) * 100}%`
        }} />
        <div style={{
          ...(compareStats.avgPoints > stats.avgPoints ? styles.comparisonBarWinning : styles.comparisonBarLosing),
          width: `${(compareStats.avgPoints / (stats.avgPoints + compareStats.avgPoints)) * 100}%`
        }} />
      </div>
      <span style={compareStats.avgPoints === stats.avgPoints ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'left'} : compareStats.avgPoints > stats.avgPoints ? styles.theirValueWinning : styles.theirValueLosing}>
        {compareStats.avgPoints}
      </span>
    </div>
  </div>

  {/* Best Round */}
  <div style={styles.comparisonRow}>
    <div style={styles.comparisonLabel}>Best Round</div>
    <div style={styles.comparisonValues}>
      <span style={stats.bestRound === compareStats.bestRound ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'right'} : stats.bestRound > compareStats.bestRound ? styles.yourValueWinning : styles.yourValueLosing}>
        {stats.bestRound}
      </span>
      <div style={styles.comparisonBarContainer}>
        <div style={{
          ...(stats.bestRound >= compareStats.bestRound ? styles.comparisonBarWinning : styles.comparisonBarLosing),
          width: `${(stats.bestRound / (stats.bestRound + compareStats.bestRound)) * 100}%`
        }} />
        <div style={{
          ...(compareStats.bestRound > stats.bestRound ? styles.comparisonBarWinning : styles.comparisonBarLosing),
          width: `${(compareStats.bestRound / (stats.bestRound + compareStats.bestRound)) * 100}%`
        }} />
      </div>
      <span style={compareStats.bestRound === stats.bestRound ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'left'} : compareStats.bestRound > stats.bestRound ? styles.theirValueWinning : styles.theirValueLosing}>
        {compareStats.bestRound}
      </span>
    </div>
  </div>

  {/* GIR % */}
  <div style={styles.comparisonRow}>
    <div style={styles.comparisonLabel}>GIR %</div>
    <div style={styles.comparisonValues}>
      <span style={stats.girPercentage === compareStats.girPercentage ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'right'} : stats.girPercentage > compareStats.girPercentage ? styles.yourValueWinning : styles.yourValueLosing}>
        {stats.girPercentage}%
      </span>
      <div style={styles.comparisonBarContainer}>
        <div style={{
          ...(stats.girPercentage >= compareStats.girPercentage ? styles.comparisonBarWinning : styles.comparisonBarLosing),
          width: `${(stats.girPercentage / (stats.girPercentage + compareStats.girPercentage)) * 100}%`
        }} />
        <div style={{
          ...(compareStats.girPercentage > stats.girPercentage ? styles.comparisonBarWinning : styles.comparisonBarLosing),
          width: `${(compareStats.girPercentage / (stats.girPercentage + compareStats.girPercentage)) * 100}%`
        }} />
      </div>
      <span style={compareStats.girPercentage === stats.girPercentage ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'left'} : compareStats.girPercentage > stats.girPercentage ? styles.theirValueWinning : styles.theirValueLosing}>
        {compareStats.girPercentage}%
      </span>
    </div>
  </div>

  {/* Fairway % */}
  <div style={styles.comparisonRow}>
    <div style={styles.comparisonLabel}>Fairways %</div>
    <div style={styles.comparisonValues}>
      <span style={stats.fairwayPercentage === compareStats.fairwayPercentage ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'right'} : stats.fairwayPercentage > compareStats.fairwayPercentage ? styles.yourValueWinning : styles.yourValueLosing}>
        {stats.fairwayPercentage}%
      </span>
      <div style={styles.comparisonBarContainer}>
        <div style={{
          ...(stats.fairwayPercentage >= compareStats.fairwayPercentage ? styles.comparisonBarWinning : styles.comparisonBarLosing),
          width: `${(stats.fairwayPercentage / (stats.fairwayPercentage + compareStats.fairwayPercentage)) * 100}%`
        }} />
        <div style={{
          ...(compareStats.fairwayPercentage > stats.fairwayPercentage ? styles.comparisonBarWinning : styles.comparisonBarLosing),
          width: `${(compareStats.fairwayPercentage / (stats.fairwayPercentage + compareStats.fairwayPercentage)) * 100}%`
        }} />
      </div>
      <span style={compareStats.fairwayPercentage === stats.fairwayPercentage ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'left'} : compareStats.fairwayPercentage > stats.fairwayPercentage ? styles.theirValueWinning : styles.theirValueLosing}>
        {compareStats.fairwayPercentage}%
      </span>
    </div>
  </div>

  {/* Up & Down % */}
{(() => {
  console.log('Up&Down Debug:', {
    yours: stats.upDownPercentage,
    yoursType: typeof stats.upDownPercentage,
    theirs: compareStats.upDownPercentage,
    theirsType: typeof compareStats.upDownPercentage,
    sum: stats.upDownPercentage + compareStats.upDownPercentage,
    yourWidth: (stats.upDownPercentage / (stats.upDownPercentage + compareStats.upDownPercentage)) * 100,
    theirWidth: (compareStats.upDownPercentage / (stats.upDownPercentage + compareStats.upDownPercentage)) * 100,
    yourColor: stats.upDownPercentage === compareStats.upDownPercentage ? 'gray' : stats.upDownPercentage > compareStats.upDownPercentage ? 'green' : 'red',
    theirColor: stats.upDownPercentage === compareStats.upDownPercentage ? 'gray' : compareStats.upDownPercentage > stats.upDownPercentage ? 'green' : 'red'
  });
  return null;
})()}
<div style={styles.comparisonRow}>
  <div style={styles.comparisonLabel}>Up & Down %</div>
  <div style={styles.comparisonValues}>
   <span style={
  stats.upDownPercentage === compareStats.upDownPercentage 
    ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'right'}
    : stats.upDownPercentage > compareStats.upDownPercentage 
    ? styles.yourValueWinning 
    : styles.yourValueLosing
}>
      {stats.upDownPercentage}%
    </span>
    <div style={styles.comparisonBarContainer}>
      <div style={{
        backgroundColor: stats.upDownPercentage > compareStats.upDownPercentage ? '#10b981' : stats.upDownPercentage < compareStats.upDownPercentage ? '#ef4444' : '#6b7280',
        width: '50%',
        height: '100%',
        opacity: stats.upDownPercentage === 0 && compareStats.upDownPercentage === 0 ? 1 : stats.upDownPercentage === 0 ? 0.3 : 1,
        transition: 'all 0.5s ease'
      }} />
      <div style={{
        backgroundColor: compareStats.upDownPercentage > stats.upDownPercentage ? '#10b981' : compareStats.upDownPercentage < stats.upDownPercentage ? '#ef4444' : '#6b7280',
        width: '50%',
        height: '100%',
        opacity: stats.upDownPercentage === 0 && compareStats.upDownPercentage === 0 ? 1 : compareStats.upDownPercentage === 0 ? 0.3 : 1,
        transition: 'all 0.5s ease'
      }} />
    </div>
    <span style={compareStats.upDownPercentage === stats.upDownPercentage ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'left'} : compareStats.upDownPercentage > stats.upDownPercentage ? styles.theirValueWinning : styles.theirValueLosing}>
      {compareStats.upDownPercentage}%
    </span>
  </div>
</div>


{/* Avg Gross (lower is better) */}
<div style={styles.comparisonRow}>
  <div style={styles.comparisonLabel}>Avg Gross</div>
  <div style={styles.comparisonValues}>
    <span style={
      stats.avgGross === compareStats.avgGross
        ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'right'}
        : stats.avgGross < compareStats.avgGross
        ? styles.yourValueWinning
        : styles.yourValueLosing
    }>
      {stats.avgGross}
    </span>
    <div style={styles.comparisonBarContainer}>
      <div style={{
        ...(stats.avgGross <= compareStats.avgGross ? styles.comparisonBarWinning : styles.comparisonBarLosing),
        width: `${(stats.avgGross / (stats.avgGross + compareStats.avgGross)) * 100}%`
      }} />
      <div style={{
        ...(compareStats.avgGross < stats.avgGross ? styles.comparisonBarWinning : styles.comparisonBarLosing),
        width: `${(compareStats.avgGross / (stats.avgGross + compareStats.avgGross)) * 100}%`
      }} />
    </div>
    <span style={
      compareStats.avgGross === stats.avgGross
        ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'left'}
        : compareStats.avgGross < stats.avgGross
        ? styles.theirValueWinning
        : styles.theirValueLosing
    }>
      {compareStats.avgGross}
    </span>
  </div>
</div>

{/* Best Gross (lower is better) */}
<div style={styles.comparisonRow}>
  <div style={styles.comparisonLabel}>Best Gross</div>
  <div style={styles.comparisonValues}>
    <span style={
      stats.bestGross === compareStats.bestGross
        ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'right'}
        : stats.bestGross < compareStats.bestGross
        ? styles.yourValueWinning
        : styles.yourValueLosing
    }>
      {stats.bestGross}
    </span>
    <div style={styles.comparisonBarContainer}>
      <div style={{
        ...(stats.bestGross <= compareStats.bestGross ? styles.comparisonBarWinning : styles.comparisonBarLosing),
        width: `${(stats.bestGross / (stats.bestGross + compareStats.bestGross)) * 100}%`
      }} />
      <div style={{
        ...(compareStats.bestGross < stats.bestGross ? styles.comparisonBarWinning : styles.comparisonBarLosing),
        width: `${(compareStats.bestGross / (stats.bestGross + compareStats.bestGross)) * 100}%`
      }} />
    </div>
    <span style={
      compareStats.bestGross === stats.bestGross
        ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'left'}
        : compareStats.bestGross < stats.bestGross
        ? styles.theirValueWinning
        : styles.theirValueLosing
    }>
      {compareStats.bestGross}
    </span>
  </div>
</div>

{/* Avg Putts (lower is better) */}
<div style={styles.comparisonRow}>
  <div style={styles.comparisonLabel}>Avg Putts</div>
  <div style={styles.comparisonValues}>
    <span style={
      parseFloat(stats.avgPutts) === parseFloat(compareStats.avgPutts)
        ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'right'}
        : parseFloat(stats.avgPutts) < parseFloat(compareStats.avgPutts)
        ? styles.yourValueWinning
        : styles.yourValueLosing
    }>
      {stats.avgPutts}
    </span>
    <div style={styles.comparisonBarContainer}>
      <div style={{
        ...(parseFloat(stats.avgPutts) <= parseFloat(compareStats.avgPutts) ? styles.comparisonBarWinning : styles.comparisonBarLosing),
        width: `${(parseFloat(stats.avgPutts) / (parseFloat(stats.avgPutts) + parseFloat(compareStats.avgPutts))) * 100}%`
      }} />
      <div style={{
        ...(parseFloat(compareStats.avgPutts) < parseFloat(stats.avgPutts) ? styles.comparisonBarWinning : styles.comparisonBarLosing),
        width: `${(parseFloat(compareStats.avgPutts) / (parseFloat(stats.avgPutts) + parseFloat(compareStats.avgPutts))) * 100}%`
      }} />
    </div>
    <span style={
      parseFloat(compareStats.avgPutts) === parseFloat(stats.avgPutts)
        ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'left'}
        : parseFloat(compareStats.avgPutts) < parseFloat(stats.avgPutts)
        ? styles.theirValueWinning
        : styles.theirValueLosing
    }>
      {compareStats.avgPutts}
    </span>
  </div>
</div>

{/* SGP Avg */}
<div style={styles.comparisonRow}>
  <div style={styles.comparisonLabel}>SGP Avg</div>
  <div style={styles.comparisonValues}>
    <span style={
      stats.seasonSGP === null || compareStats.seasonSGP === null
        ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'right'}
        : stats.seasonSGP === compareStats.seasonSGP
        ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'right'}
        : stats.seasonSGP > compareStats.seasonSGP
        ? styles.yourValueWinning
        : styles.yourValueLosing
    }>
      {stats.seasonSGP !== null
        ? (stats.seasonSGP > 0 ? '+' : '') + stats.seasonSGP
        : '—'}
    </span>
    <div style={styles.comparisonBarContainer}>
      {stats.seasonSGP !== null && compareStats.seasonSGP !== null ? (
        <>
          <div style={{
            backgroundColor: stats.seasonSGP > compareStats.seasonSGP ? '#10b981' : stats.seasonSGP < compareStats.seasonSGP ? '#ef4444' : '#6b7280',
            width: '50%',
            height: '100%',
            transition: 'all 0.5s ease'
          }} />
          <div style={{
            backgroundColor: compareStats.seasonSGP > stats.seasonSGP ? '#10b981' : compareStats.seasonSGP < stats.seasonSGP ? '#ef4444' : '#6b7280',
            width: '50%',
            height: '100%',
            transition: 'all 0.5s ease'
          }} />
        </>
      ) : (
        <>
          <div style={{ backgroundColor: '#2d2d2d', width: '50%', height: '100%' }} />
          <div style={{ backgroundColor: '#2d2d2d', width: '50%', height: '100%' }} />
        </>
      )}
    </div>
    <span style={
      stats.seasonSGP === null || compareStats.seasonSGP === null
        ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'left'}
        : compareStats.seasonSGP === stats.seasonSGP
        ? {fontSize: '20px', fontWeight: '700', color: '#6b7280', textAlign: 'left'}
        : compareStats.seasonSGP > stats.seasonSGP
        ? styles.theirValueWinning
        : styles.theirValueLosing
    }>
      {compareStats.seasonSGP !== null
        ? (compareStats.seasonSGP > 0 ? '+' : '') + compareStats.seasonSGP
        : '—'}
    </span>
  </div>
</div>
</div>

            <button onClick={() => setShowComparison(false)} style={styles.closeComparisonButton}>
              Close Comparison
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    overflowY: 'auto',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  card: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.25s ease'
  },
  cardLarge: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 30px',
    borderBottom: '1px solid #2d2d2d',
    flexShrink: 0,
    background: '#1e1e1e',
    borderRadius: '12px 12px 0 0'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#d4af37',
    margin: 0,
    letterSpacing: '-0.02em'
  },
  subtitle: {
    fontSize: '14px',
    color: '#9ca3af',
    margin: '5px 0 0 0',
    fontWeight: '400'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: '1px solid #d4af37',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#d4af37',
    padding: '5px 15px',
    borderRadius: '6px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '30px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#6b7280',
    fontSize: '16px',
    fontWeight: '400'
  },
  photoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#0f0f0f',
    borderRadius: '12px',
    border: '1px solid #2d2d2d'
  },
  photoContainer: {
    marginBottom: '15px'
  },
  photo: {
    width: '150px',
    height: '150px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid #d4af37',
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
  },
  photoPlaceholder: {
    width: '150px',
    height: '150px',
    borderRadius: '50%',
    backgroundColor: '#0f0f0f',
    border: '3px solid #d4af37',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '64px',
    fontWeight: '700',
    color: '#d4af37',
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
  },
  fileInput: {
    display: 'none'
  },
  uploadButton: {
    padding: '12px 24px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    transition: 'all 0.3s ease'
  },
  section: {
    marginBottom: '35px'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '1px solid #2d2d2d',
    letterSpacing: '-0.01em'
  },
  trophiesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '20px'
  },
  trophyCard: {
    backgroundColor: '#0f0f0f',
    border: '2px solid #d4af37',
    borderRadius: '12px',
    padding: '25px',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    cursor: 'default'
  },
  trophyIcon: {
    fontSize: '48px',
    marginBottom: '10px'
  },
  trophyName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#d4af37',
    marginBottom: '5px',
    letterSpacing: '-0.01em'
  },
  trophyDescription: {
    fontSize: '13px',
    color: '#9ca3af',
    marginBottom: '8px',
    fontWeight: '400'
  },
  trophyYear: {
    fontSize: '12px',
    color: '#d4af37',
    opacity: 0.7,
    fontWeight: '500'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '15px'
  },
  statCard: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '8px',
    textAlign: 'center',
    border: '1px solid #2d2d2d'
  },
  statLabel: {
    fontSize: '11px',
    color: '#6b7280',
    marginBottom: '8px',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#d4af37',
    letterSpacing: '-0.02em'
  },
  progressBars: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  progressItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '15px',
    fontWeight: '600',
    color: '#ffffff'
  },
  progressValue: {
    color: '#d4af37',
    fontWeight: '700'
  },
  progressBar: {
    width: '100%',
    height: '12px',
    backgroundColor: '#2d2d2d',
    borderRadius: '6px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#d4af37',
    transition: 'width 0.3s ease'
  },
  funStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '20px'
  },
  funStatCard: {
    backgroundColor: '#0f0f0f',
    color: 'white',
    padding: '25px',
    borderRadius: '12px',
    textAlign: 'center',
    border: '2px solid #d4af37'
  },
  funStatIcon: {
    fontSize: '36px',
    marginBottom: '10px'
  },
  funStatValue: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#d4af37',
    marginBottom: '5px',
    letterSpacing: '-0.02em'
  },
  funStatLabel: {
    fontSize: '13px',
    color: '#9ca3af',
    fontWeight: '400'
  },
  trophyCardSpecial: {
    backgroundColor: '#0f0f0f',
    border: '3px solid #d4af37',
    borderRadius: '12px',
    padding: '25px',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    cursor: 'default',
    boxShadow: '0 8px 24px rgba(212, 175, 55, 0.3)'
  },
  trophyNameSpecial: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#d4af37',
    marginBottom: '5px',
    letterSpacing: '-0.01em'
  },
  specialBadge: {
    marginTop: '10px',
    padding: '6px 12px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  selectOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  selectCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '400px',
    width: '90%',
    maxHeight: '70vh',
    overflowY: 'auto',
    border: '1px solid #2d2d2d'
  },
  selectTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '20px',
    textAlign: 'center',
    letterSpacing: '-0.01em'
  },
  playersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px'
  },
  playerSelectButton: {
    padding: '15px',
    backgroundColor: '#0f0f0f',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    transition: 'all 0.3s ease'
  },
  cancelSelectButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#2d2d2d',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    color: '#9ca3af',
    transition: 'all 0.3s ease'
  },
  comparisonOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  comparisonCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '700px',
    width: '90%',
    maxHeight: '80vh',
    overflowY: 'auto',
    border: '1px solid #2d2d2d'
  },
  comparisonHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    borderBottom: '1px solid #d4af37',
    paddingBottom: '20px'
  },
  comparisonPlayer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    flex: 1
  },
  comparisonPhoto: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #d4af37'
  },
  comparisonPhotoPlaceholder: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#0f0f0f',
    border: '2px solid #d4af37',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    fontWeight: '700',
    color: '#d4af37'
  },
  comparisonPlayerName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#ffffff',
    letterSpacing: '-0.01em'
  },
  comparisonVs: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#d4af37',
    padding: '0 20px'
  },
  comparisonStats: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    marginBottom: '30px'
  },
  comparisonRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  comparisonLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    textAlign: 'center',
    marginBottom: '5px'
  },
  comparisonValues: {
    display: 'grid',
    gridTemplateColumns: '60px 1fr 60px',
    alignItems: 'center',
    gap: '15px'
  },
  yourValueWinning: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#10b981',
    textAlign: 'right'
  },
  yourValueLosing: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#ef4444',
    textAlign: 'right'
  },
  theirValueWinning: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#10b981',
    textAlign: 'left'
  },
  theirValueLosing: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#ef4444',
    textAlign: 'left'
  },
  comparisonBarWinning: {
    backgroundColor: '#10b981',
    transition: 'width 0.5s ease'
  },
  comparisonBarLosing: {
    backgroundColor: '#ef4444',
    transition: 'width 0.5s ease'
  },
  comparisonBarContainer: {
    display: 'flex',
    height: '30px',
    borderRadius: '15px',
    overflow: 'hidden',
    border: '1px solid #2d2d2d'
  },
  closeComparisonButton: {
    width: '100%',
    padding: '15px',
    backgroundColor: '#2d2d2d',
    color: '#9ca3af',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  compareButton: {
    backgroundColor: '#d4af37',
    border: '1px solid #d4af37',
    fontSize: '14px',
    cursor: 'pointer',
    color: '#0f0f0f',
    padding: '8px 16px',
    borderRadius: '6px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
};

export default MyProfile;